import { useState, useEffect, useRef, useCallback } from 'react';

export interface RouteSegment {
  fromStopId: string;
  toStopId: string;
  duration: number; // seconds
  distance: number; // meters
  geometry: GeoJSON.LineString;
}

interface StopInput {
  id: string;
  latitude: number;
  longitude: number;
  stop_order: number;
}

interface UseRoutesBetweenStopsResult {
  routes: RouteSegment[];
  loading: boolean;
  error: string | null;
  totalDuration: number; // seconds
  totalDistance: number; // meters
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';
const MAPBOX_BASE = 'https://api.mapbox.com/directions/v5/mapbox/driving';

/** Build a cache key from all stop coordinates for the multi-waypoint request */
function routeCacheKey(stops: StopInput[]): string {
  return stops.map(s => `${s.latitude},${s.longitude}`).join(';');
}

/** Build a fingerprint of the stops array for change detection */
function stopsFingerprint(stops: StopInput[]): string {
  return stops
    .sort((a, b) => a.stop_order - b.stop_order)
    .map(s => `${s.id}:${s.latitude},${s.longitude}`)
    .join('|');
}

/** Fetch all route legs in a single multi-waypoint Mapbox Directions request */
async function fetchMultiWaypointRoute(
  sortedStops: StopInput[]
): Promise<RouteSegment[] | null> {
  const coords = sortedStops
    .map(s => `${s.longitude},${s.latitude}`)
    .join(';');
  const url = `${MAPBOX_BASE}/${coords}?overview=full&geometries=geojson&steps=true&access_token=${MAPBOX_TOKEN}`;

  const res = await fetch(url);
  if (!res.ok) return null;

  const data = await res.json();
  if (data.code !== 'Ok' || !data.routes?.length) return null;

  const route = data.routes[0];
  const legs = route.legs;

  if (!legs || legs.length !== sortedStops.length - 1) return null;

  // Extract per-leg geometry from steps since overview=full gives one merged geometry
  const segments: RouteSegment[] = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];

    // Build leg geometry from steps
    const legCoords: [number, number][] = [];
    if (leg.steps) {
      for (const step of leg.steps) {
        if (step.geometry?.coordinates) {
          // Avoid duplicating the junction point between steps
          const coords = step.geometry.coordinates;
          const startIdx = legCoords.length > 0 ? 1 : 0;
          for (let j = startIdx; j < coords.length; j++) {
            legCoords.push(coords[j]);
          }
        }
      }
    }

    segments.push({
      fromStopId: sortedStops[i].id,
      toStopId: sortedStops[i + 1].id,
      duration: leg.duration,
      distance: leg.distance,
      geometry: {
        type: 'LineString',
        coordinates: legCoords,
      },
    });
  }

  return segments;
}

export function useRoutesBetweenStops(stops: StopInput[]): UseRoutesBetweenStopsResult {
  const [routes, setRoutes] = useState<RouteSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheRef = useRef<Map<string, RouteSegment[]>>(new Map());
  const lastFingerprintRef = useRef<string>('');

  const fetchRoutes = useCallback(async (sortedStops: StopInput[]) => {
    if (sortedStops.length < 2) {
      setRoutes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Check cache for this exact set of stops
    const cacheKey = routeCacheKey(sortedStops);
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      // Update stop IDs in case they changed (same coords, different stop objects)
      const updated = cached.map((seg, i) => ({
        ...seg,
        fromStopId: sortedStops[i].id,
        toStopId: sortedStops[i + 1].id,
      }));
      setRoutes(updated);
      setLoading(false);
      return;
    }

    try {
      const segments = await fetchMultiWaypointRoute(sortedStops);

      if (segments && segments.length > 0) {
        cacheRef.current.set(cacheKey, segments);
        setRoutes(segments);
        setError(null);
      } else {
        setRoutes([]);
        setError('Unable to calculate driving routes');
      }
    } catch {
      setRoutes([]);
      setError('Unable to calculate driving routes');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const sortedStops = [...stops].sort((a, b) => a.stop_order - b.stop_order);
    const fingerprint = stopsFingerprint(sortedStops);

    if (fingerprint === lastFingerprintRef.current) return;
    lastFingerprintRef.current = fingerprint;

    // Debounce to avoid hammering OSRM during reorders
    const timer = setTimeout(() => fetchRoutes(sortedStops), 200);
    return () => clearTimeout(timer);
  }, [stops, fetchRoutes]);

  const totalDuration = routes.reduce((sum, r) => sum + r.duration, 0);
  const totalDistance = routes.reduce((sum, r) => sum + r.distance, 0);

  return { routes, loading, error, totalDuration, totalDistance };
}
